import { useTranslation } from "react-i18next";
import { Button } from "@resideo/blueprint-oneportal";
import styles from "./ManageDevicesSearch.module.css";
import SearchIcon from "@/assets/icons/search.svg?react";
import CloseIcon from "@/assets/icons/close.svg?react";
import FilterIcon from "@/assets/icons/filter.svg?react";
import {
  ManageDevicesColumnsMenu,
  type ColumnOption,
} from "../ManageDevicesColumnsMenu/ManageDevicesColumnsMenu";
import type { DeviceColumnKey } from "../ManageDevicesTable/ManageDevicesTable";

export interface ManageDevicesSearchProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  /** Number of active filters; shown as a badge on the Filters button. */
  filterCount?: number;
  /** Fired when the Filters button is clicked. Placeholder for future filtering UI. */
  onFiltersClick?: () => void;
  /** Toggleable column definitions rendered in the Columns menu. */
  columns?: ColumnOption[];
  /** Fired when a column is toggled in the Columns menu. */
  onColumnToggle?: (key: DeviceColumnKey) => void;
  /** Fired with the new key order after drag-and-drop in the Columns menu. */
  onColumnReorder?: (orderedKeys: DeviceColumnKey[]) => void;
}

export const ManageDevicesSearch = ({
  value,
  onChange,
  placeholder,
  filterCount = 0,
  onFiltersClick,
  columns = [],
  onColumnToggle = () => {},
  onColumnReorder,
}: ManageDevicesSearchProps) => {
  const { t } = useTranslation();
  const resolvedPlaceholder = placeholder ?? t("manageDevices.searchDevices");
  return (
    <div className={styles.searchContainer}>
      <div className={styles.searchWrapper}>
        <SearchIcon className={styles.searchIcon} aria-hidden="true" />
        <input
          type="text"
          className={styles.searchInput}
          placeholder={resolvedPlaceholder}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          aria-label={resolvedPlaceholder}
        />
        {value.length > 0 && (
          <button
            type="button"
            className={styles.clearSearchButton}
            onClick={() => onChange("")}
            aria-label={t("manageLocations.clearSearch")}
          >
            <CloseIcon aria-hidden="true" />
          </button>
        )}
      </div>

      <div className={styles.actions}>
        <Button
          variant="highlightTertiary"
          className={styles.actionButton}
          onClick={onFiltersClick}
        >
          <FilterIcon className={styles.actionIcon} aria-hidden="true" />
          <span className={styles.labelGroup}>
            <span className={styles.actionLabel}>{t("manageDevices.filters")}</span>
            {filterCount > 0 && (
              <span
                className={styles.actionCount}
                aria-label={t("manageDevices.activeFiltersCount", {
                  count: filterCount,
                })}
              >
                {filterCount}
              </span>
            )}
          </span>
        </Button>

        <ManageDevicesColumnsMenu
          columns={columns}
          onToggle={onColumnToggle}
          onReorder={onColumnReorder}
        />
      </div>
    </div>
  );
};

export default ManageDevicesSearch;
